import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FileDown } from "lucide-react";
import { RevealLine } from "@/components/RevealLine";
import { LegalCrossLinks } from "@/pages/Terms";

const SECTIONS = [
  {
    title: "Introduction",
    body: "Offcut is designed to provide useful digital tools for building, carpentry, construction and related activities. Our Acceptable Use Policy helps keep the Services safe, reliable and available for everyone.",
  },
  {
    title: "Use Offcut Lawfully",
    body: "Do not use the Services for unlawful, fraudulent or harmful activities.",
  },
  {
    title: "Respect Other People's Rights",
    body: "Do not upload or use content that infringes another person's intellectual property, privacy or other legal rights.",
  },
  {
    title: "Keep Your Account Secure",
    body: "Do not share, sell, rent or provide unauthorised access to your account. Team accounts may only be used by authorised users within the purchased seat limit.",
  },
  {
    title: "Don't Interfere With The Services",
    body: "Do not hack, disrupt, overload, circumvent security measures or attempt to gain unauthorised access to Offcut or its systems.",
  },
  {
    title: "No Abusive Automation",
    body: "Do not use bots, scripts or automated systems in a way that places unreasonable demands on Offcut or bypasses technical or subscription restrictions.",
  },
  {
    title: "Use AI Responsibly",
    body: "Do not use Offcut's AI or voice features for unlawful, fraudulent, harmful or deceptive purposes.",
  },
  {
    title: "Verify Important Information",
    body: "AI-generated content, calculations and other outputs should be independently reviewed before being used for construction, safety, compliance, professional or other important purposes.",
  },
  {
    title: "Fair Use",
    body: "Usage must be consistent with the features and subscription plan you have purchased. Reasonable usage limits may apply to protect service availability and fair access.",
  },
  {
    title: "Enforcement",
    body: "Where necessary, we may warn, restrict, suspend or terminate accounts that breach these requirements or threaten the security or reliability of the Services.",
  },
  {
    title: "Changes",
    body: "We may update the policy as Offcut develops, including when new features, usage limits or technical capabilities are introduced.",
  },
];

export default function AcceptableUse() {
  return (
    <motion.main
      data-testid="acceptable-use-page"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
      className="relative z-10 mx-auto max-w-3xl px-6 pb-32 pt-32 sm:px-10"
    >
      <Link
        to="/"
        data-testid="page-logo-home-link"
        aria-label="Back to home"
        className="block w-fit rounded-lg transition-transform duration-300 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
      >
        <img
          src="/chippymate-logo.png"
          alt="Offcut — back to home"
          className="h-auto w-28"
        />
      </Link>
      <p className="mt-2 text-xs font-semibold uppercase tracking-[0.35em] text-white/50">
        Offcut - ChippyMate
      </p>
      <h1 className="font-brand mt-3 text-6xl tracking-wide text-white sm:text-7xl">
        <RevealLine>Acceptable Use Policy</RevealLine>
      </h1>
      <p className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
        Last updated: 2026
      </p>
      <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base">
        This summary is provided for convenience only. The full Acceptable Use
        Policy forms part of the ChippyMate - Offcut Terms and Conditions and
        contains the detailed requirements governing use of the Services.
      </p>

      <motion.a
        data-testid="aup-pdf-download"
        href="/acceptable-use-policy.pdf"
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-8 inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-black/30 px-6 py-3 text-sm font-bold text-white backdrop-blur-xl transition-colors duration-300 hover:border-white/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
      >
        <FileDown className="h-4 w-4" />
        Full Acceptable Use Policy (PDF)
      </motion.a>

      <div className="mt-16 space-y-12">
        {SECTIONS.map((section, i) => (
          <motion.section
            key={section.title}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
            className="border-t border-white/10 pt-8"
          >
            <div className="flex items-baseline gap-5">
              <span className="font-brand text-2xl text-white/35">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h2 className="font-brand text-3xl tracking-wide text-white sm:text-4xl">
                {section.title}
              </h2>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-white/70 sm:text-base">
              {section.body}
            </p>
          </motion.section>
        ))}
      </div>

      <motion.p
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
        className="mt-16 border-t border-white/10 pt-8 text-sm leading-relaxed text-white/70 sm:text-base"
      >
        If you have any questions about the Acceptable Use Policy, please reach
        out via the{" "}
        <Link
          to="/contact"
          className="font-semibold text-white underline decoration-white/40 underline-offset-4 transition-colors duration-300 hover:decoration-white"
        >
          Contact Us
        </Link>{" "}
        page.
      </motion.p>

      <LegalCrossLinks current="/acceptable-use" />
    </motion.main>
  );
}
